import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { getCreditUsageDisplayState } from '~/components/CreditUsageMeter/utils';
import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';
import { getGiftCreditsBalance } from '~/utils/creditUtils';
import { getAllowCreditOverage } from '~/utils/getAllowCreditOverage';

type CreditUsageBannerSubscription = Pick<
  EnhancedSubscription,
  'pricingModel' | 'isOnEnterprisePlan' | 'isOnBusinessPlan'
>;

export const getCreditUsageBannerAlertCandidate = ({
  creditStats,
  subscription,
  canEditPlan,
  showPlansModal,
}: {
  creditStats: CreditUsageStats;
  subscription: CreditUsageBannerSubscription;
  canEditPlan: boolean;
  showPlansModal: () => void;
}) => {
  // Credit-model plans alert from the allotment even when stats omit enforcement.
  // Fixed plans alert only once the backend enforces their credits.
  // Overage keeps spending going past the allotment, so those banners are misleading.
  if (
    !creditStats.allocated ||
    (subscription.pricingModel !== 'credit' && !creditStats.isEnforced) ||
    getAllowCreditOverage(creditStats)
  ) {
    return null;
  }

  let candidate:
    | ({ useSessionStorage?: boolean } & {
        title: string;
        description: string;
        ctaButton?: { text: string } & ({ onClick: () => void } | { href: string; newTab?: boolean });
      })
    | null = null;

  const giftCreditsBalance = getGiftCreditsBalance({
    pricingModel: subscription.pricingModel,
    statsGiftCreditsBalance: creditStats.giftCreditsBalance,
  });
  const totalCredits = creditStats.allocated + giftCreditsBalance;
  const creditUsageState = getCreditUsageDisplayState({
    allowCreditOverage: getAllowCreditOverage(creditStats),
    totalCredits,
    used: creditStats.used,
  });

  switch (creditUsageState) {
    case 'depleted': {
      candidate = {
        useSessionStorage: canEditPlan,
        title:
          subscription.pricingModel === 'credit'
            ? 'Your workspace is out of credits, so uploading and AI are currently paused.'
            : 'Your workspace is out of credits.',
        description: canEditPlan
          ? 'Upgrade now to keep your work flowing.'
          : 'Contact your admin to upgrade and keep things moving.',
        ctaButton: {
          text: 'Upgrade Plan',
          onClick: showPlansModal,
        },
      };
      break;
    }

    case '95%': {
      let description =
        subscription.pricingModel === 'credit'
          ? 'Upgrade now to keep your work flowing without interruption.'
          : 'Upgrade now to unlock more and keep your work flowing.';

      if (!canEditPlan) {
        description =
          subscription.pricingModel === 'credit'
            ? 'Contact your admin to keep your work flowing without interruption.'
            : 'Contact your admin to keep your work flowing.';
      }

      candidate = {
        title: 'Your workspace is nearly out of credits.',
        description,
        ctaButton: {
          text: 'Upgrade Plan',
          onClick: showPlansModal,
        },
      };
      break;
    }
    default:
      return null;
  }

  if (!canEditPlan) {
    candidate.ctaButton = undefined;
  } else if (subscription.isOnEnterprisePlan || subscription.isOnBusinessPlan) {
    candidate.ctaButton = {
      text: 'Talk to Sales',
      onClick: showPlansModal,
    };
  }

  return {
    ...candidate,
    creditUsageState,
  };
};
